import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight, Star, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { searchCourses, getAllCourses, useCourses } from "@/utils/courseService";
import { COURSE_TAGS } from "@/data/courses";
import { COURSE_PATHWAYS } from "@/constants/testIds";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";

// AI and Microsoft are hidden here for now -- keep them out of the pill row
// without touching the shared COURSE_TAGS list other pages may still use.
const VISIBLE_TAGS = COURSE_TAGS.filter((t) => t !== "AI" && t !== "Microsoft");

// The list on the right should read in the same left-to-right order as the
// pill row above it (SAP, then Workday, then AI for Leaders) instead of
// whatever order the underlying course data happens to be in. Anything
// outside that set (e.g. the hidden AI/Microsoft tags) sorts after, keeping
// its original relative order.
const CATEGORY_SORT_ORDER = ["SAP", "Workday", "AI for Leaders"];
function categoryRank(course) {
  const idx = CATEGORY_SORT_ORDER.findIndex((t) => (course.tags || []).includes(t));
  return idx === -1 ? CATEGORY_SORT_ORDER.length : idx;
}

/**
 * 02 - Course Pathways.
 *
 * UX (rewritten for clarity):
 *  - One BIG featured card on the left showing the current pathway.
 *  - A vertical list on the right where each course is a compact row.
 *  - Clicking a row promotes it into the featured card. Hover only highlights
 *    the row visually , it does NOT swap the featured card. The featured card
 *    itself is a link straight to the course detail page.
 *  - No auto-rotation. The active row is clearly marked with a ring + arrow.
 *  - Search + tag filter narrow the list; whenever the filter changes, the
 *    featured card falls back to the first result.
 */
export default function FeaturedCoursePathways() {
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("All");
  const [focusSlug, setFocusSlug] = useState(null);

  const { courses: liveCourses } = useCourses();
  const results = useMemo(() => {
    return searchCourses(query, { tag })
      .filter((course) => course.category !== "bundles")
      .sort((a, b) => categoryRank(a) - categoryRank(b));
  }, [query, tag, liveCourses]);

  // Whenever filters change, snap focus to first result.
  useEffect(() => {
    setFocusSlug(results[0]?.slug || null);
  }, [query, tag, results]);

  const focused =
    results.find((c) => c.slug === focusSlug) ||
    results[0] ||
    getAllCourses().find((c) => c.featured) ||
    getAllCourses()[0];

  const promoteCard = (slug) => {
    setFocusSlug(slug);
    trackEvent(ANALYTICS_EVENTS.COURSE_PATHWAY_FOCUSED, { slug });
  };

  return (
    <section
      data-testid={COURSE_PATHWAYS.root}
      id="course-pathways"
      className="relative py-12 lg:py-18 bg-tint-alt"
    >
      <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
              02 . Course pathways
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-[34px] font-bold text-ink mt-3 leading-[1.1] text-balance max-w-2xl">
              Find your pathway across SAP, Workday, AI, Microsoft, and bundles.
            </h2>
          </div>
          <div className="flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 w-full max-w-md shadow-sm">
            <Search size={16} className="text-ink-soft" />
            <input
              data-testid={COURSE_PATHWAYS.search}
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                trackEvent(ANALYTICS_EVENTS.COURSE_SEARCHED, { q: e.target.value });
              }}
              placeholder="Search SAP, Workday, AI, Copilot..."
              className="flex-1 bg-transparent text-sm placeholder:text-ink-soft/60 focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {VISIBLE_TAGS.map((t) => {
            const active = tag === t;
            return (
              <button
                key={t}
                type="button"
                data-testid={COURSE_PATHWAYS.tag(t)}
                onClick={() => setTag(t)}
                className={`rounded-full px-4 py-1.5 text-[12.5px] font-semibold transition-colors ${
                  active
                    ? "bg-royal-deep text-white"
                    : "border border-line text-ink bg-white hover:border-royal hover:text-royal"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>

        {!focused ? (
          <div className="mt-10 rounded-2xl border border-line bg-white p-10 text-center text-ink-soft shadow-sm">
            No courses match your filters.
          </div>
        ) : (
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Featured card */}
            <Link
              to={`/courses/${focused.slug}`}
              data-testid={COURSE_PATHWAYS.card(focused.slug)}
              data-cat={focused.category}
              className="lg:col-span-7 relative overflow-hidden rounded-2xl bg-bg-dark text-white p-7 lg:p-10 block shadow-[0_28px_70px_-30px_rgba(15,42,115,0.55)] transition-transform hover:-translate-y-0.5"
            >
              <div className="absolute inset-x-0 top-0 h-1 cat-topbar" aria-hidden />
              <div className="absolute inset-0 bg-grid-dark opacity-40" aria-hidden />
              <div
                className="absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-30 blur-3xl"
                style={{ background: "var(--cat-accent)" }}
                aria-hidden
              />
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--cyan)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--royal-deep)]">
                  <Star size={12} /> Featured pathway
                </span>
                <h3 className="font-display text-2xl md:text-3xl lg:text-[36px] font-bold mt-5 leading-tight max-w-md text-balance">
                  {focused.title}
                </h3>
                <p className="mt-4 max-w-md text-white/75 leading-relaxed">{focused.description}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {(focused.tags || []).map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-white/15 px-3 py-1 text-[11px] font-mono-ui tracking-wider text-white/80"
                    >
                      {t}
                    </span>
                  ))}
                  {(focused.format || []).map((f) => (
                    <span
                      key={f}
                      className="rounded-full bg-white/5 px-3 py-1 text-[11px] font-mono-ui tracking-wider text-white/80"
                    >
                      {f}
                    </span>
                  ))}
                </div>

                <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                  {focused.category !== "bundles" && (
                    <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">
                      <p className="font-mono-ui text-[9.5px] tracking-[0.16em] text-white/50 uppercase">Duration</p>
                      <p className="text-[13px] font-semibold text-white mt-0.5">{focused.duration || "-"}</p>
                    </div>
                  )}
                  {focused.category !== "bundles" && (
                    <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5">
                      <p className="font-mono-ui text-[9.5px] tracking-[0.16em] text-white/50 uppercase">Next Cohort</p>
                      <p className="text-[13px] font-semibold text-white mt-0.5">{focused.schedule || "-"}</p>
                    </div>
                  )}
                  <div className="rounded-lg border border-[var(--cyan)]/40 bg-[var(--cyan)]/10 px-3 py-2.5">
                    <p className="font-mono-ui text-[9.5px] tracking-[0.16em] text-[var(--cyan)] uppercase">Starts at</p>
                    <p className="text-[13px] font-bold text-[var(--cyan)] mt-0.5">{focused.price || "-"}</p>
                  </div>
                </div>

                <div className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[var(--cyan)]">
                  <span data-testid={COURSE_PATHWAYS.viewDetails(focused.slug)}>View Details</span>
                  <ArrowUpRight size={14} />
                </div>
              </div>
            </Link>

            {/* Course list , clicking a row promotes it into the featured card */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-line bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-2.5 border-b border-line flex items-center justify-between bg-bg-alt/50">
                  <p className="font-mono-ui text-[10.5px] uppercase tracking-[0.18em] text-royal">
                    Choose a pathway
                  </p>
                  <p className="text-[11px] text-ink-soft">
                    {results.length} course{results.length === 1 ? "" : "s"}
                  </p>
                </div>
                <div className="max-h-[520px] overflow-y-auto divide-y divide-line">
                  {results.slice(0, 24).map((c, i) => {
                    const active = c.slug === focused.slug;
                    const tint = ((i % 6) + 1);
                    return (
                      <button
                        key={c.slug}
                        type="button"
                        data-testid={COURSE_PATHWAYS.card(c.slug)}
                        data-cat={c.category}
                        data-tint={active ? undefined : String(tint)}
                        onClick={() => promoteCard(c.slug)}
                        className={`group w-full text-left px-4 py-3 flex items-start gap-3 transition-colors border-b border-black/5 last:border-b-0 ${
                          active
                            ? "bg-cyan-soft/80 cat-ring"
                            : "hover:brightness-95"
                        }`}
                      >
                        <span
                          className="mt-1 inline-flex h-2 w-2 shrink-0 rounded-full"
                          style={{ background: "var(--cat-accent)" }}
                          aria-hidden
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-mono-ui text-[9.5px] uppercase tracking-[0.18em] text-royal-deep">
                            {c.category} · {c.level}
                          </p>
                          <p className={`font-display text-[14px] font-bold mt-0.5 leading-tight truncate ${
                            active ? "text-royal-deep" : "text-ink group-hover:text-royal-deep"
                          }`}>
                            {c.title}
                          </p>
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {(c.tags || []).slice(0, 2).map((t) => (
                              <Badge
                                key={t}
                                variant="secondary"
                                className="bg-white/70 text-royal-deep border border-[var(--cyan)]/30 text-[10px] py-0"
                              >
                                {t}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <ArrowRight
                          size={14}
                          className={`mt-1.5 shrink-0 transition-opacity ${
                            active ? "opacity-100 text-royal-deep" : "opacity-0 group-hover:opacity-60 text-ink-soft"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
              <p className="mt-2.5 text-[11px] text-ink-soft">
                Click a course to preview it above. Click the featured card to open the detail page.
              </p>
            </div>
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:border-royal hover:text-royal shadow-sm"
          >
            Browse all courses <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
